import { useEffect, useState } from 'react'

import { useGetIngestQuestionsQuery } from '@axilon-apps/domains-ontology-ui-state'

import { newestPendingQuestionId, shouldRequestSessionAttention } from './sessionTabRules'

/** Survives ChatPage remounting when the URL moves between plant and ingest. */
const seenQuestionIds: Record<string, string> = {}

export function resetIngestSessionAttentionSeen(): void {
  for (const key of Object.keys(seenQuestionIds)) delete seenQuestionIds[key]
}

/**
 * Shared pending-question observer for the rail and the chat page. Questions
 * refetch on agent-write wake-ups, so an idle job costs nothing.
 *
 * A question is "new" only once across every surface, so a caller that cannot
 * act on it passes `requestAttention: false` and reads `pendingQuestionId`
 * alone — otherwise it would consume the signal the rail needs to auto-focus.
 */
export function useIngestSessionAttention(
  session: { id: string } | undefined,
  options?: { requestAttention?: boolean }
): {
  pendingQuestionId: string | null | undefined
  attentionKey: number
} {
  const requestAttention = options?.requestAttention ?? true
  const jobId = session?.id
  const { data } = useGetIngestQuestionsQuery(jobId ?? '', {
    skip: !jobId,
    refetchOnMountOrArgChange: true
  })
  const questionId = data ? newestPendingQuestionId(data.pending) : undefined
  const [pendingByJob, setPendingByJob] = useState<Record<string, string | null>>({})
  const [attentionKey, setAttentionKey] = useState(0)

  useEffect(() => {
    if (!jobId || questionId === undefined) return
    setPendingByJob((current) =>
      current[jobId] === questionId ? current : { ...current, [jobId]: questionId }
    )
  }, [jobId, questionId])

  const pendingQuestionId = jobId ? pendingByJob[jobId] : undefined

  useEffect(() => {
    if (!requestAttention) return
    if (!jobId || pendingQuestionId === undefined || pendingQuestionId === null) return
    if (!shouldRequestSessionAttention(seenQuestionIds[jobId], pendingQuestionId)) {
      return
    }
    seenQuestionIds[jobId] = pendingQuestionId
    setAttentionKey((key) => key + 1)
  }, [jobId, pendingQuestionId, requestAttention])

  return { pendingQuestionId, attentionKey }
}
